// src/pages/Services/branchService.ts
//
// Public-facing "branches" (a.k.a show rooms / warehouses in the admin app)
// used by the client site's Branches page. Reads from the same `warehouses`
// Firestore collection the admin app writes to — firestore.rules already
// allows public read on that collection.
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { db } from '../Firebase/Config';

const WAREHOUSES_COLLECTION = 'warehouses';

// ── Operating hours ───────────────────────────────────────────────────────────
// Mirrors admin/src/enums/product.ts — set per-day so a branch can be open,
// say, Mon-Sat and closed Sunday rather than one flat pair applied every day.
export const DAYS_OF_WEEK = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;
export type DayOfWeek = typeof DAYS_OF_WEEK[number];

export const DAY_LABELS: Record<DayOfWeek, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

export interface DayHours {
  open: string;
  close: string;
  closed: boolean;
}

export interface BranchOperatingHours {
  timezone: string;
  monday: DayHours;
  tuesday: DayHours;
  wednesday: DayHours;
  thursday: DayHours;
  friday: DayHours;
  saturday: DayHours;
  sunday: DayHours;
}

export interface BranchAddress {
  street: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

export interface BranchContact {
  phone: string;
  email: string;
  manager: string;
}

export interface Branch {
  id: string;
  name: string;
  code: string;
  description: string;
  address: BranchAddress;
  contact: BranchContact;
  isActive: boolean;
  displayOnWebsite: boolean;
  operatingHours?: BranchOperatingHours;
}

// Formats a "HH:mm" 24h time string as a friendly 12h label, e.g. "09:00" -> "9:00 AM".
// Falls back to the raw value if it doesn't look like a valid time.
export const formatTime12h = (time: string): string => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time || '');
  if (!match) return time || '—';
  const hour = parseInt(match[1], 10);
  const minute = match[2];
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:${minute} ${period}`;
};

// Some branch documents may still have the old flat
// `{ open, close, timezone }` operating-hours shape from before hours became
// per-day. Detect that case and surface it as "not set" (undefined) instead
// of guessing at a mapping — the UI already handles undefined gracefully.
function normalizeOperatingHours(raw: any): BranchOperatingHours | undefined {
  if (!raw) return undefined;

  const hasPerDayData = DAYS_OF_WEEK.some(day => raw[day] && typeof raw[day] === 'object');
  if (!hasPerDayData) return undefined;

  const normalized = { timezone: raw.timezone || 'UTC' } as BranchOperatingHours;
  DAYS_OF_WEEK.forEach(day => {
    const dayData = raw[day];
    (normalized as any)[day] = dayData
      ? {
          open: dayData.open || '09:00',
          close: dayData.close || '18:00',
          closed: !!dayData.closed,
        }
      : { open: '09:00', close: '18:00', closed: false };
  });
  return normalized;
}

export class BranchService {
  /**
   * Branches that are both active and explicitly opted in to display on the
   * public website (`displayOnWebsite === true`).
   */
  static async getDisplayedBranches(): Promise<Branch[]> {
    try {
      const q = query(
        collection(db, WAREHOUSES_COLLECTION),
        where('isActive', '==', true),
        where('displayOnWebsite', '==', true),
        orderBy('name', 'asc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          name: data.name || '',
          code: data.code || '',
          description: data.description || '',
          address: data.address || { street: '', city: '', state: '', country: '', zipCode: '' },
          contact: data.contact || { phone: '', email: '', manager: '' },
          isActive: data.isActive !== false,
          displayOnWebsite: data.displayOnWebsite === true,
          operatingHours: normalizeOperatingHours(data.operatingHours),
        } as Branch;
      });
    } catch (error) {
      console.error('Error fetching displayed branches:', error);
      throw error;
    }
  }
}
